import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OpeningTimesIcon from '@propertypal/shared/src/resources/icons/new-homes/opening-times-icon-sm.svg';
import { HomepageTakeover } from '@propertypal/shared/src/types/homepageTakeover';
import ModalWrapper from '@propertypal/web-ui/src/modal/ModalWrapper';
import { useRouter } from 'next/router';
import React, { FunctionComponent, VideoHTMLAttributes, useRef, ReactNode } from 'react';
import { useTheme } from 'styled-components';
import {
  Container as InfoBar,
  Address,
  Content as InfoBarContent,
  CustomLink,
  CustomText,
  ImageWrapper,
  MobileText,
} from '../animation/ImageInfoBar.style';
import { Container, Content, ImageBox } from '../animation/ImageSlideshow.style';
import PropertyPrice from '../property/PropertyPrice';
import { Text } from '../typography';
import {
  VideoBox,
  FullscreenContainer,
  FullscreenBtn,
  BackToHome,
  Title,
  IconWrapper,
  ShowHomeMob,
} from './Takeover.style';

interface Props {
  takeover: HomepageTakeover;
  children?: ReactNode;
}

const Takeover: FunctionComponent<Props> = (props) => {
  const router = useRouter();
  const fullscreen = router.query.fullscreen === 'true';
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const property = props.takeover.listing;
  const listingLogo = property?.listingLogos ? property?.listingLogos[0].standard : undefined;
  const shareURL = property?.path;
  const price = property?.price;

  const hasShowHome = property && property.showHomeOpeningTime && !property.showHomeOpeningTime.byAppointment;

  const poster =
    props.takeover.altImage?.url ||
    (props.takeover.video && props.takeover.video.coverImageUrl) ||
    (property && property.images && property.images.length > 0 && property.images[0].url) ||
    undefined;

  const videoProps: VideoHTMLAttributes<HTMLVideoElement> = {
    autoPlay: true,
    playsInline: true,
    controls: fullscreen,
    loop: true,
    poster,
  };

  const onClose = () => {
    // remove fullscreen query
    const { fullscreen: fllscrn, ...query } = router.query;
    router.push({ pathname: router.pathname, query }, undefined, {
      shallow: true,
    });
  };

  if (fullscreen && props.takeover.video)
    return (
      <ModalWrapper scrollSelector="#fullscreen-vid" show={fullscreen} onClose={onClose}>
        <FullscreenContainer id="fullscreen-vid">
          <VideoBox {...videoProps} data-testid="takeoverVideoFullscreen" fullscreen>
            <source src={props.takeover.video.url} type="video/mp4" />
          </VideoBox>
          <BackToHome onClick={onClose}>
            <FontAwesomeIcon icon={faChevronLeft} color={theme.white} style={{ marginRight: '5px' }} /> Back to homepage
          </BackToHome>
        </FullscreenContainer>
      </ModalWrapper>
    );

  return (
    <Container minHeight={props.takeover.title ? 480 : 360} data-testid="homepage-takeover">
      <Content>
        {props.takeover.video && (
          <>
            <VideoBox {...videoProps} muted data-testid="takeoverVideo" ref={videoRef}>
              <source src={props.takeover.video.url} type="video/mp4" />
            </VideoBox>
            <FullscreenBtn
              onClick={() => {
                // update url so that when user hits browser back button it brings them back to homepage
                router.push({ pathname: router.pathname, query: { ...router.query, fullscreen: true } }, undefined, {
                  shallow: true,
                });
              }}
            >
              <FontAwesomeIcon icon={faPlay} color={theme.white} style={{ fontSize: 11, marginRight: '5px' }} /> Play
              video fullscreen
            </FullscreenBtn>
          </>
        )}

        {/* If only altImg is passed, will fallback to this if no video, or video errors */}
        {poster && (
          <ImageBox
            active
            data-testid="alt-image"
            src={poster}
            sizes="(max-width: 1200px) 1024px, 1600px"
            alt={property?.displayAddress || 'Superfeature Image'}
          />
        )}

        {props.takeover.title && <Title data-testid="takeover-title">{props.takeover.title}</Title>}

        {hasShowHome && (
          <ShowHomeMob data-testid="show-home-openings-mob">
            <Text color={theme.white} fontSize={14}>
              <Address>Show Home Opening:&nbsp;</Address>
              <Text as="span" color={theme.white} fontSize={14} fontWeight="bold">
                {property && property.showHomeOpeningTime && property.showHomeOpeningTime.defaultText}
              </Text>
            </Text>
          </ShowHomeMob>
        )}

        {shareURL && (
          <InfoBar active>
            <CustomLink href={shareURL} data-testid="info-bar" $hasShowHome={hasShowHome}>
              <InfoBarContent>
                {listingLogo && (
                  <ImageWrapper data-testid="listing-logo">
                    <img src={listingLogo} alt="Agent Logo" height={30} width="auto" />
                  </ImageWrapper>
                )}
                <CustomText color={theme.white} fontSize={14}>
                  <Address>{property?.displayAddress} -&nbsp;</Address>
                  {price && <PropertyPrice as="span" price={price} fontSize={14} color={theme.white} />}
                </CustomText>
                <MobileText>
                  <Text color={theme.white} fontSize={14}>
                    {property?.displayAddressLine1}
                  </Text>
                  {price && <PropertyPrice as="span" price={price} fontSize={12} color={theme.white} />}
                </MobileText>
              </InfoBarContent>

              {hasShowHome && (
                <InfoBarContent $hasShowHome={hasShowHome} data-testid="show-home-openings">
                  <IconWrapper>
                    <OpeningTimesIcon />
                  </IconWrapper>
                  <CustomText color={theme.white} fontSize={14}>
                    <Address>Show Home Opening:&nbsp;</Address>
                    <Text as="span" color={theme.white} fontSize={14} fontWeight="bold">
                      {property && property.showHomeOpeningTime && property.showHomeOpeningTime.defaultText}
                    </Text>
                  </CustomText>
                </InfoBarContent>
              )}
            </CustomLink>
          </InfoBar>
        )}

        {props.children}
      </Content>
    </Container>
  );
};

export default Takeover;
